import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import logoImage from '../assets/Frame 1000005895.png';
import upgradeButtonImage from '../assets/Frame 9.png';
import projectsButtonImage from '../assets/Frame 5.png';
import languageButtonImage from '../assets/Frame 1000005885 (1).png';
import profileButtonImage from '../assets/Frame 2 (1).png';
import LanguageModal from '../components/LanguageModal';
import ProfileMenu from '../components/ProfileMenu';
import LoginModal from '../components/LoginModal.js';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState({ name: 'English', code: 'en-IN', flag: '🇮🇳' });
  const [currentUser, setCurrentUser] = useState(null);
  
  // Get the user data when component mounts
  React.useEffect(() => {
    const getUserFromStorage = () => {
      const storedUser = localStorage.getItem('currentUser');
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setCurrentUser(parsedUser);
          return parsedUser;
        } catch (error) {
          console.error('Error parsing stored user:', error);
          return null;
        }
      }
      return null;
    };
    
    getUserFromStorage();
  }, []);
  
  const goToProjects = () => {
    navigate('/projects');
  };
  
  const goToUpgrade = () => {
    navigate('/upgrade');
  };

  const openLanguageModal = () => {
    setIsLanguageModalOpen(true);
  };

  const closeLanguageModal = () => {
    setIsLanguageModalOpen(false);
  };
  
  const openLoginModal = () => {
    setIsLoginModalOpen(true);
  };
  
  const closeLoginModal = () => {
    setIsLoginModalOpen(false);
  };
  
  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };
  
  const closeProfileMenu = () => {
    setIsProfileMenuOpen(false);
  };
  
  const handleLanguageChange = (language) => {
    setCurrentLanguage(language);
    console.log(`Language changed to: ${language.name} (${language.code})`);
  };
  
  // Handle profile button click based on login status
  const handleProfileButtonClick = () => {
    if (currentUser) {
      toggleProfileMenu();
    } else {
      openLoginModal();
    }
  };

  const navigateToSearch = () => {
    navigate('/');
  };

  return (
    <PageContainer>
      <Header>
        <LogoSection>
          <Logo src={logoImage} alt="Total AI" onClick={navigateToSearch}/>
        </LogoSection>
        <NavSection>
          <UpgradeButton onClick={goToUpgrade}>
            <UpgradeButtonImg src={upgradeButtonImage} alt="Upgrade" />
          </UpgradeButton>
          <ProjectsButton onClick={goToProjects}>
            <ProjectsButtonImg src={projectsButtonImage} alt="My Projects" />
          </ProjectsButton>
          <ProfileButton onClick={handleProfileButtonClick}>
            <ProfileButtonImg src={profileButtonImage} alt="Profile" />
          </ProfileButton>
        </NavSection>
      </Header>

      <ContentContainer>
        <ContentHeader>
          <SectionTitle>Privacy Policy</SectionTitle>
          <LastUpdated>Last Updated: Apriil, 2025</LastUpdated>
        </ContentHeader>

        <PrivacyContent>
          <Section>
            <SectionHeading>1. Introduction</SectionHeading>
            <Paragraph>
              At BattleXO AI, we respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your information when you use our website, mobile applications, and services.
            </Paragraph>
            <Paragraph>
              By using our platform, you agree to the collection and use of information in accordance with this policy. We will not use or share your information with anyone except as described in this Privacy Policy.
            </Paragraph>
          </Section>
          
          <Section>
            <SectionHeading>2. Information We Collect</SectionHeading>
            <Paragraph>
              We collect several types of information from and about users of our platform, including:
            </Paragraph>
            <SubSection>
              <SubHeading>2.1 Personal Information</SubHeading>
              <Paragraph>
                We may collect personally identifiable information such as your name, email address, and profile picture. This information is collected when you:
              </Paragraph>
              <BulletList>
                <ListItem>Register for an account</ListItem>
                <ListItem>Sign in with Google or other third-party services</ListItem>
                <ListItem>Subscribe to our newsletter or promotional content</ListItem>
                <ListItem>Contact us through our support channels</ListItem>
                <ListItem>Participate in surveys or contests</ListItem>
              </BulletList>
            </SubSection>
            
            <SubSection>
              <SubHeading>2.2 Usage Data</SubHeading>
              <Paragraph>
                We may also collect information about how you access and use our platform, including:
              </Paragraph>
              <BulletList>
                <ListItem>Your device's IP address</ListItem>
                <ListItem>Browser type and version</ListItem>
                <ListItem>Operating system</ListItem>
                <ListItem>Pages visited and time spent on those pages</ListItem>
                <ListItem>Unique device identifiers</ListItem>
                <ListItem>Interaction patterns and preferences</ListItem>
              </BulletList>
            </SubSection>
            
            <SubSection>
              <SubHeading>2.3 User Content</SubHeading>
              <Paragraph>
                We collect and store content that you create, upload, or receive when using our services. This includes any projects, games, or prompts you create using our AI tools, as well as your chat history with our AI assistants.
              </Paragraph>
            </SubSection>
          </Section>
          
          <Section>
            <SectionHeading>3. How We Use Your Information</SectionHeading>
            <Paragraph>
              We use the information we collect for various purposes, including:
            </Paragraph>
            <BulletList>
              <ListItem>To provide, maintain, and improve our services</ListItem>
              <ListItem>To personalize your experience and deliver content relevant to your interests</ListItem>
              <ListItem>To process payments and provide you with customer support</ListItem>
              <ListItem>To send you technical notices, updates, security alerts, and support messages</ListItem>
              <ListItem>To communicate with you about products, services, offers, and events</ListItem>
              <ListItem>To monitor usage of our platform for research and analytics</ListItem>
              <ListItem>To protect our platform and users from fraudulent, harmful, or unlawful activity</ListItem>
              <ListItem>To improve our AI models and machine learning algorithms</ListItem>
            </BulletList>
          </Section>
          
          <Section>
            <SectionHeading>4. Data Sharing and Disclosure</SectionHeading>
            <Paragraph>
              We may share your information in the following situations:
            </Paragraph>
            <SubSection>
              <SubHeading>4.1 With Service Providers</SubHeading>
              <Paragraph>
                We may share your information with third-party vendors, service providers, contractors, or agents who perform services for us or on our behalf.
              </Paragraph>
            </SubSection>
            
            <SubSection>
              <SubHeading>4.2 For Business Transfers</SubHeading>
              <Paragraph>
                If we are involved in a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction.
              </Paragraph>
            </SubSection>
            
            <SubSection>
              <SubHeading>4.3 For Legal Compliance</SubHeading>
              <Paragraph>
                We may disclose your information if we believe it is necessary to comply with a legal obligation, protect our rights, or ensure the safety of our users or the public.
              </Paragraph>
            </SubSection>
            
            <SubSection>
              <SubHeading>4.4 With Your Consent</SubHeading>
              <Paragraph>
                We may share your information with third parties when we have your consent to do so.
              </Paragraph>
            </SubSection>
          </Section>
          
          <Section>
            <SectionHeading>5. Data Security</SectionHeading>
            <Paragraph>
              We implement appropriate technical and organizational measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </Paragraph>
          </Section>
          
          <Section>
            <SectionHeading>6. Your Rights and Choices</SectionHeading>
            <Paragraph>
              You have several rights regarding your personal information:
            </Paragraph>
            <BulletList>
              <ListItem><Bold>Access:</Bold> You can request copies of your personal information.</ListItem>
              <ListItem><Bold>Rectification:</Bold> You can request that we correct inaccurate information about you.</ListItem>
              <ListItem><Bold>Deletion:</Bold> You can request that we delete your personal information.</ListItem>
              <ListItem><Bold>Restriction:</Bold> You can request that we restrict the processing of your information.</ListItem>
              <ListItem><Bold>Data Portability:</Bold> You can request a copy of your information in a structured, machine-readable format.</ListItem>
              <ListItem><Bold>Objection:</Bold> You can object to our processing of your information.</ListItem>
            </BulletList>
            <Paragraph>
              To exercise any of these rights, please contact us at privacy@battlexo.ai.
            </Paragraph>
          </Section>
          
          <Section>
            <SectionHeading>7. Cookies and Tracking Technologies</SectionHeading>
            <Paragraph>
              We use cookies and similar tracking technologies to track the activity on our platform and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our platform.
            </Paragraph>
          </Section>
          
          <Section>
            <SectionHeading>8. Children's Privacy</SectionHeading>
            <Paragraph>
              Our platform is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and you are aware that your child has provided us with personal information, please contact us.
            </Paragraph>
          </Section>
          
          <Section>
            <SectionHeading>9. International Data Transfers</SectionHeading>
            <Paragraph>
              Your information may be transferred to, and maintained on, computers located outside of your state, province, country, or other governmental jurisdiction. If you are located outside the United States and choose to provide information to us, please note that we transfer the information to the United States and process it there.
            </Paragraph>
          </Section>
          
          <Section>
            <SectionHeading>10. Changes to This Privacy Policy</SectionHeading>
            <Paragraph>
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
            </Paragraph>
          </Section>
          
          <Section>
            <SectionHeading>11. Contact Us</SectionHeading>
            <Paragraph>
              If you have any questions about this Privacy Policy, please contact us at privacy@battlexo.ai.
            </Paragraph>
          </Section>
          
          <Section>
            <TermsLink onClick={() => navigate('/terms')}>View our Terms and Conditions</TermsLink>
          </Section>
        </PrivacyContent>
      </ContentContainer>

      <LanguageModal 
        isOpen={isLanguageModalOpen} 
        onClose={closeLanguageModal} 
        onLanguageChange={handleLanguageChange}
      />
      
      <ProfileMenu
        isOpen={isProfileMenuOpen}
        onClose={closeProfileMenu}
      />
      
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={closeLoginModal}
      />
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  max-width: 100%;
  min-height: 100vh;
  background-color: #ffffff;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #eaeaea;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
`;

const Logo = styled.img`
  height: 24px;
  cursor: pointer;
`;

const NavSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const UpgradeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const UpgradeButtonImg = styled.img`
  height: 36px;
  width: auto;
`;

const ProjectsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const ProjectsButtonImg = styled.img`
  height: 36px;
  width: auto;
`;

const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const ButtonImage = styled.img`
  height: 36px;
  width: auto;
`;

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const ProfileButtonImg = styled.img`
  height: 36px;
  width: auto;
`;

const ContentContainer = styled.main`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 24px;
`;

const ContentHeader = styled.div`
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eaeaea;
`;

const SectionTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  background: linear-gradient(90deg, #E80694, #0782FE);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 8px;
`;

const LastUpdated = styled.p`
  font-size: 14px;
  color: #666;
`;

const PrivacyContent = styled.div`
  color: #333;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 32px;
`;

const SectionHeading = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #23232D;
`;

const SubSection = styled.div`
  margin-bottom: 24px;
`;

const SubHeading = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #23232D;
`;

const Paragraph = styled.p`
  margin-bottom: 16px;
  font-size: 16px;
`;

const BulletList = styled.ul`
  margin-bottom: 16px;
  padding-left: 40px;
`;

const ListItem = styled.li`
  margin-bottom: 8px;
`;

const Bold = styled.span`
  font-weight: 600;
`;

const TermsLink = styled.div`
  display: inline-block;
  color: #0782FE;
  cursor: pointer;
  text-decoration: underline;
  margin-top: 24px;
  font-size: 16px;
  
  &:hover {
    color: #E80694;
  }
`;

export default PrivacyPolicy; 